import {Component, Element, Event, type EventEmitter, h, Listen, Prop, State} from '@stencil/core';
import {GenericCardTitle} from '../../generic-subcomponents/generic-card-title/generic-card-title';
import {GenericCardLabels} from '../../generic-subcomponents/generic-card-labels/generic-card-labels';
import {GenericCardContainer} from '../../generic-subcomponents/generic-card-container/generic-card-container';
import type {SearchResultClickEvent} from '../../../search-tab/SearchResultClickEvent';
import {GenericCardEventsLayer} from '../../generic-subcomponents/generic-card-event-layer/generic-card-event-layer';
import type {SearchApiGenericResult} from '../../../../../api/model/response/documents/SearchResult';
import type {SearchProduct} from '../../../../../api/model/response/documents/public/Product';
import {makeLogger} from '../../../../../logger/logger';
import {ResultCardType} from '../product-card/helpers/helpers';
import {defaultFileDescription, defaultLabelsList} from '../../utils/defaultLabelsList';
import {Strategies} from '../../../../strategies/Strategies';
import {
  GenericCardFilesDownload
} from '../../generic-subcomponents/generic-card-files-download/generic-card-files-download';
import type {DocumentDownloadEvent} from '../../../result-cards/utils/DocumentDownloadEvent';
import {DialogFilesDownload} from '../../generic-subcomponents/generic-card-files-download/dialog-files-download';
import {isMobile} from '../../../../../utils/media';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';


const {info} = makeLogger({pathToCurrentFile: 'components/result-page/new-result-cards/uiaas-search-result-document-v1.tsx'});


@Component({
  tag: 'uiaas-search-result-document-v1',
  shadow: true,
  styleUrl: 'uiaas-search-result-document-v1.scss',
})

export class UiaasSearchResultDocumentV1 {

    @Prop() readonly doc: SearchApiGenericResult;
    @Prop() readonly gridView: boolean;
    @Prop() readonly isDashboardView: boolean = false;
    @Prop() readonly srt: boolean = false;
    @Prop({mutable: true}) labelProvider?: labelProviderInterface;
    @Prop() readonly strategies: Strategies;

    /**
     * Position of search result in a list
     * (required for CTR-related analysis)
     */
    @Prop() readonly index: number = null;

    @Prop() readonly language: string;

    @Prop() readonly country: string;

    @State() accessibilityMode;

    @State() isDialogOpen = false;

    @State() isMobile = isMobile();

    private toggleDialog = (isOpen:boolean) =>{
        this.isDialogOpen = isOpen;
    };

    @Listen('uiaas:accessibility', { target: 'window' })
    handleAccessibility(ev) {
        this.accessibilityMode = ev.detail;
    }

    @Element() el;

    connectedCallback() {
        const htmlElement = document.querySelector('html');
        this.accessibilityMode = htmlElement.classList.contains('se2-accessibility');
    }

    componentWillLoad() {
        if (!this.labelProvider) {
          this.labelProvider = (name, fallback) => {
              info(
                'LabelProvider not provided in DocumentCardNew component',
                [name, fallback]
              );
              return fallback;
          };
        }
    }

    @Event({
      eventName: 'search:ResultCardClick',
      composed: true,
      cancelable: true,
      bubbles: true
    })
      resultCardClickEventEmitter: EventEmitter<SearchResultClickEvent>;

    @Event({
      eventName: 'search:DocumentDownloadEvent',
      composed: true,
      cancelable: true,
      bubbles: true
    })
      documentDownloadEmitter: EventEmitter<DocumentDownloadEvent>;

    private documentDownloadAnalytics = (name, type, id) => {
        this.documentDownloadEmitter.emit({
          event: 'download',
          documentName: name,
          documentType:  type,
          documentId: id,
        });
    };

    render() {
        const isPremium = this.doc?.audience && this.doc?.audience === 'Private';
        const isRegistered = this.doc?.audience && this.doc?.audience === 'Registered';
        const isMySchneider = this.strategies?.strategyData?.cardType?.params?.brand === 'myschneider' || false;
        const attachmentsExist = !isRegistered && this.doc?.attachments && this.doc.attachments?.length > 0;

        return (
          <div>
            <GenericCardEventsLayer clickEmitter={this.resultCardClickEventEmitter} resutCardType={ResultCardType.Document}
              doc={this.doc as unknown as SearchProduct} index={this.index}>
              <GenericCardContainer gridView={this.gridView} isDashboardView={this.isDashboardView} doc={this.doc} srt={this.srt}
                labelProvider={(e, f) => this.labelProvider(e, f)} strategies={this.strategies}>
                <section class={'product-result-card__text-content-section'}>
                  {isPremium ? this.privateDocIconLabelRender() : ''}
                  <div class="product-result-card__text-content-section--top">
                    <GenericCardTitle url={this.doc.url} text={this.doc.title} strategies={this.strategies}/>
                  </div>
                  <GenericCardLabels labelProvider={(e, f) => this.labelProvider(e, f)} labels={defaultLabelsList(this.doc, this.accessibilityMode).reverse()}/>
                  <GenericCardLabels labelProvider={(e, f) => this.labelProvider(e, f)} isLabelProviderOn={true} labels={defaultFileDescription(this.doc.version, this.language, this.doc.docLocale ? this.doc.docLocale : [])}/>
                  {(isMySchneider || attachmentsExist) && (
                    <span>
                      <div class={'product-card__divider'}>
                        <se-divider option={'horizontal'} color={'standard'}/>
                      </div>
                      <GenericCardFilesDownload toggleDialog={this.toggleDialog} documentDownloadAnalytics={this.documentDownloadAnalytics} labelProvider={(e, f) => this.labelProvider(e, f)} documents={this.doc}/>
                    </span>
                  )}
                </section>
              </GenericCardContainer>
            </GenericCardEventsLayer>
            {(isMySchneider || attachmentsExist) ? <DialogFilesDownload
              clickEmitter={this.resultCardClickEventEmitter}
              files={this.doc}
              isDialogOpen={this.isDialogOpen}
              labelProvider={this.labelProvider}
              toggleDialog={this.toggleDialog}
              documentDownloadAnalytics={this.documentDownloadAnalytics}
              index={this.index}/> : '' }
          </div>
        );
    }

    private privateDocIconLabelRender() {
        return <div class={'private-doc-premium-tag'}>
          <qds-tag
            text="Premium"
            status="success"
            size="small"
            icon-name="crown"
          />
        </div>;
    }
}
